import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Coupon {
  id: string;
  code: string;
  type: "percentage" | "fixed";
  value: number;
  minPurchase?: number;
  usageLimit: number;
  usageCount: number;
  startDate: string;
  endDate: string;
  scope: ("pos" | "online")[];
  status: "active" | "expired" | "scheduled" | "disabled";
}

const MOCK_COUPONS: Coupon[] = [
  { id: "1", code: "WELCOME20", type: "percentage", value: 20, minPurchase: 50, usageLimit: 100, usageCount: 45, startDate: "2024-01-01", endDate: "2024-12-31", scope: ["pos", "online"], status: "active" },
  { id: "2", code: "SAVE10", type: "fixed", value: 10, usageLimit: 500, usageCount: 234, startDate: "2024-01-01", endDate: "2024-06-30", scope: ["online"], status: "active" },
  { id: "3", code: "FLASH50", type: "percentage", value: 50, minPurchase: 100, usageLimit: 50, usageCount: 50, startDate: "2024-01-10", endDate: "2024-01-15", scope: ["pos", "online"], status: "expired" },
  { id: "4", code: "SUMMER25", type: "percentage", value: 25, usageLimit: 200, usageCount: 0, startDate: "2024-06-01", endDate: "2024-08-31", scope: ["pos", "online"], status: "scheduled" },
  { id: "5", code: "VIP15", type: "percentage", value: 15, usageLimit: -1, usageCount: 89, startDate: "2024-01-01", endDate: "2024-12-31", scope: ["pos"], status: "active" },
];

export default function CouponListSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredCoupons = MOCK_COUPONS.filter((coupon) => {
    const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || coupon.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: Coupon["status"]) => {
    const styles = {
      active: "bg-emerald-500/20 text-emerald-500",
      expired: "bg-red-500/20 text-red-500",
      scheduled: "bg-blue-500/20 text-blue-500",
      disabled: "bg-gray-500/20 text-gray-500",
    };
    const labels = {
      active: t("posCoupons.statusActive"),
      expired: t("posCoupons.statusExpired"),
      scheduled: t("posCoupons.statusScheduled"),
      disabled: t("posCoupons.statusDisabled"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const activeCoupons = MOCK_COUPONS.filter((c) => c.status === "active").length;
  const totalRedemptions = MOCK_COUPONS.reduce((acc, c) => acc + c.usageCount, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posCoupons.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posCoupons.subtitle")}</p>
        </div>
        <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
          <i className="ri-add-line"></i>
          {t("posCoupons.createCoupon")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posCoupons.activeCoupons")}</span>
            <i className="ri-coupon-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{activeCoupons}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posCoupons.totalRedemptions")}</span>
            <i className="ri-ticket-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalRedemptions}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posCoupons.totalSavings")}</span>
            <i className="ri-money-dollar-circle-line text-pink-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>$4,250</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posCoupons.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="all">{t("posCoupons.allStatuses")}</option>
            <option value="active">{t("posCoupons.statusActive")}</option>
            <option value="expired">{t("posCoupons.statusExpired")}</option>
            <option value="scheduled">{t("posCoupons.statusScheduled")}</option>
            <option value="disabled">{t("posCoupons.statusDisabled")}</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.code")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.discount")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.usage")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.validity")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.scope")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posCoupons.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.map((coupon) => (
                <tr key={coupon.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-bold ${themeClasses.text}`}>{coupon.code}</span>
                      <button className={`p-1 rounded ${themeClasses.tableRowHover}`}>
                        <i className={`ri-file-copy-line ${themeClasses.textSecondary}`}></i>
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-primary-500 font-bold">
                      {coupon.type === "percentage" ? `${coupon.value}%` : `$${coupon.value}`}
                    </span>
                    {coupon.minPurchase && (
                      <p className={`text-xs ${themeClasses.textSecondary}`}>
                        Min: ${coupon.minPurchase}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={themeClasses.text}>{coupon.usageCount}</span>
                    <span className={themeClasses.textSecondary}>
                      /{coupon.usageLimit === -1 ? "∞" : coupon.usageLimit}
                    </span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.textSecondary} text-sm`}>
                    {coupon.startDate} - {coupon.endDate}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-1">
                      {coupon.scope.includes("pos") && (
                        <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-500">POS</span>
                      )}
                      {coupon.scope.includes("online") && (
                        <span className="px-2 py-1 rounded text-xs bg-violet-500/20 text-violet-500">WEB</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(coupon.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-edit-line"></i>
                      </button>
                      <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-bar-chart-line"></i>
                      </button>
                      <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
